!function () {
  'use strict';

  const PRODUCT_PAGE_SELECTOR = '.product__page';
  const FORM_WRAPPER_SELECTOR = '[data-form-wrapper]';
  const HEADER_STICKY_SELECTOR = '[data-header-sticky]';
  const PRODUCT_MEDIA_LIST_SELECTOR = '[data-product-media-list]';
  const STICKY_CLASS = 'is-sticky';
  const POPOUT_MAX_HEIGHT_VAR = '--select-popout-max-height';
  const POPOUT_TOGGLE_SELECTOR = '[data-popout-toggle]';
  const POPOUT_LIST_SELECTOR = '[data-popout-list]';
  const POPOUT_LIST_TOP_CLASS = 'popout-list--top';
  const POPOUT_MAX_SPACING = 8;

  if (customElements.get('product-sticky')) return;

  customElements.define(
    'product-sticky',
    class extends HTMLElement {
      constructor() {
        super();
        this.formWrapper = this.querySelector(FORM_WRAPPER_SELECTOR);
        this.stickyScrollTop = 0;
        this.scrollLastPosition = 0;
        this.stickyDefaultTop = 0;
        this.currentPoint = 0;
        this.defaultTopBottomSpacings = 30;
        this.scrollTopPosition = window.scrollY;
        this.scrollDirectionDown = true;
        this.requestAnimationSticky = null;
        this.stickyFormLoad = true;
        this.stickyFormLastHeight = null;
        this.onChangeCounter = 0;
        this.scrollEvent = (t) => this.scrollEvents(t);
        this.resizeEvent = (t) => this.resizeEvents(t);
        this.stickyFormEvent = (t) => this.stickyFormEvents(t);
        this.popoutToggleClickEvent = (t) => this.onPopoutToggleClick(t);
      }

      connectedCallback() {
        this.stickyScrollCheck();
        document.addEventListener('theme:resize', this.resizeEvent);
        this.addEventListener('click', this.popoutToggleClickEvent);
        this.updatePopoutMaxHeight();

        if (theme.variables.productPageSticky) {
          this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition());
          this.formWrapper.addEventListener('theme:form:sticky', this.stickyFormEvent);
          document.addEventListener('theme:scroll', this.scrollEvent);
        }
      }

      disconnectedCallback() {
        document.removeEventListener('theme:resize', this.resizeEvent);
        this.removeEventListener('click', this.popoutToggleClickEvent);

        if (theme.variables.productPageSticky) {
          document.removeEventListener('theme:scroll', this.scrollEvent);
          this.formWrapper?.removeEventListener('theme:form:sticky', this.stickyFormEvent);
        }
      }

      onPopoutToggleClick(event) {
        const toggle = event.target.closest(POPOUT_TOGGLE_SELECTOR);
        if (!toggle) return;

        const popout = toggle.closest('popout-select');
        if (!popout) return;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.updatePopoutMaxHeight(popout));
        });
      }

      getActivePopouts() {
        return [...this.querySelectorAll('popout-select')].filter((popout) =>
          popout.querySelector(`${POPOUT_TOGGLE_SELECTOR}[aria-expanded="true"]`),
        );
      }

      clearPopoutMaxHeight(popout) {
        const list = popout.querySelector(POPOUT_LIST_SELECTOR);
        popout.style.removeProperty(POPOUT_MAX_HEIGHT_VAR);
        list?.style.removeProperty('max-height');
      }

      updatePopoutMaxHeight(targetPopout = null) {
        const popouts = targetPopout ? [targetPopout] : this.getActivePopouts();

        popouts.forEach((popout) => {
          const list = popout.querySelector(POPOUT_LIST_SELECTOR);
          const toggle = popout.querySelector(POPOUT_TOGGLE_SELECTOR);

          if (!list || !toggle) return;

          if (!list.classList.contains(POPOUT_LIST_TOP_CLASS)) {
            this.clearPopoutMaxHeight(popout);
            return;
          }

          const { headerHeight } = window.theme.readHeights();
          const topBound = Math.max(this.getBoundingClientRect().top, headerHeight);
          const toggleTop = toggle.getBoundingClientRect().top;
          const maxHeight = Math.floor(toggleTop - topBound - POPOUT_MAX_SPACING);

          if (maxHeight <= 0) {
            this.clearPopoutMaxHeight(popout);
            return;
          }

          const value = `${maxHeight}px`;
          popout.style.setProperty(POPOUT_MAX_HEIGHT_VAR, value);
          list.style.maxHeight = value;
        });
      }

      stickyFormEvents(t) {
        this.removeAnimationFrameSticky();
        this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition(t));
      }

      scrollEvents(t) {
        this.scrollTopPosition = t.detail.position;
        this.scrollDirectionDown = t.detail.down;

        if (!this.requestAnimationSticky) {
          this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition());
        }
      }

      resizeEvents() {
        this.stickyScrollCheck();
        document.removeEventListener('theme:scroll', this.scrollEvent);
        this.formWrapper?.removeEventListener('theme:form:sticky', this.stickyFormEvent);
      }

      stickyScrollCheck() {
        const pageFormWrapper = this.querySelector(`${PRODUCT_PAGE_SELECTOR} ${FORM_WRAPPER_SELECTOR}`);

        if (!pageFormWrapper) return;

        if (window.theme.isMobile()) {
          theme.variables.productPageSticky = false;
          pageFormWrapper.classList.remove(STICKY_CLASS);
          return;
        }

        const formWrapper = this.querySelector(FORM_WRAPPER_SELECTOR);
        const mediaList = this.querySelector(PRODUCT_MEDIA_LIST_SELECTOR);

        if (!formWrapper || !mediaList) return;

        if (formWrapper.offsetHeight < mediaList.offsetHeight) {
          theme.variables.productPageSticky = true;
          pageFormWrapper.classList.add(STICKY_CLASS);
        } else {
          theme.variables.productPageSticky = false;
          pageFormWrapper.classList.remove(STICKY_CLASS);
        }
      }

      calculateStickyPosition(t = null) {
        if (document.documentElement.hasAttribute('data-scroll-locked')) {
          this.removeAnimationFrameSticky();
          return;
        }

        const hasDetail = Boolean(t && t.detail);
        const isAccordionChange = Boolean(hasDetail && t.detail.element && t.detail.element === 'accordion');
        const formHeight = this.formWrapper.offsetHeight;
        const maxScrollTop = window.innerHeight - formHeight - this.defaultTopBottomSpacings;
        const scrollDelta = Math.abs(this.scrollTopPosition - this.scrollLastPosition);

        if (this.scrollDirectionDown) {
          this.stickyScrollTop -= scrollDelta;
        } else {
          this.stickyScrollTop += scrollDelta;
        }

        if (this.stickyFormLoad) {
          if (document.querySelector(HEADER_STICKY_SELECTOR)) {
            const { headerHeight } = window.theme.readHeights();
            this.stickyDefaultTop = headerHeight;
          } else {
            this.stickyDefaultTop = this.defaultTopBottomSpacings;
          }

          this.stickyScrollTop = this.stickyDefaultTop;
        }

        this.stickyScrollTop = Math.min(Math.max(this.stickyScrollTop, maxScrollTop), this.stickyDefaultTop);

        const stickyDelta = this.stickyScrollTop - this.currentPoint;
        this.currentPoint = this.stickyFormLoad ? this.stickyScrollTop : this.currentPoint + stickyDelta * 0.5;
        this.formWrapper.style.setProperty('--sticky-top', `${this.currentPoint}px`);
        this.updatePopoutMaxHeight();

        this.scrollLastPosition = this.scrollTopPosition;
        this.stickyFormLoad = false;

        const shouldContinue =
          (isAccordionChange && this.onChangeCounter <= 10) ||
          (isAccordionChange && this.stickyFormLastHeight !== formHeight) ||
          (this.stickyScrollTop !== this.currentPoint && this.requestAnimationSticky);

        if (isAccordionChange) {
          this.onChangeCounter += 1;

          if (this.stickyFormLastHeight !== formHeight) {
            this.onChangeCounter = 11;
          }
        }

        if (shouldContinue) {
          this.requestAnimationSticky = requestAnimationFrame(() => this.calculateStickyPosition(t));
        } else if (this.requestAnimationSticky) {
          this.removeAnimationFrameSticky();
        }

        this.stickyFormLastHeight = formHeight;
      }

      removeAnimationFrameSticky() {
        if (!this.requestAnimationSticky) return;

        cancelAnimationFrame(this.requestAnimationSticky);
        this.requestAnimationSticky = null;
        this.onChangeCounter = 0;
      }
    },
  );
}();
